import React, { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { deleteReaction, getReactions } from 'flow/actions/chat-actions';
import BaseModal from 'flow/components/ui-components/BaseModal';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import {
    selectExtensions,
    selectOwnExtensionKey,
} from 'flow/selectors/ExtensionSelectors';
import { selectMessageReactions } from 'flow/selectors/chat-message-selectors';
import {
    ChatMessageKey,
    ChatSessionKey,
    ExtensionKey,
} from 'flow/types/EntityKeys';

interface Props {
    type?: string;
    keys?: {
        chatSessionKey: ChatSessionKey;
        chatMessageKey: ChatMessageKey;
        threadParentKey?: ChatMessageKey;
    };
}

const ReactionsModal = ({ type, keys }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();

    const [tab, setTab] = useState(type);

    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);
    const extensions = useAppSelector(selectExtensions);

    const reactions = useAppSelector((state) =>
        selectMessageReactions(state, keys.chatSessionKey, keys.chatMessageKey),
    );

    const totalReactions = reactions.reduce(
        (sum, reaction) => sum + reaction.get('count'),
        0,
    );

    useEffect(() => {
        dispatch(getReactions(keys.chatSessionKey, keys.chatMessageKey));
    }, [dispatch, keys.chatMessageKey, keys.chatSessionKey, totalReactions]);

    const getOwnReactions = () => {
        const filtered = reactions.filter((reaction) =>
            reaction.get('users').includes(ownExtensionKey),
        );
        if (tab === 'all') return filtered;
        return filtered.filter((reaction) => reaction.get('type') === tab);
    };

    const getReactors = () => {
        if (tab === 'all') return reactions;
        return reactions.filter((reaction) => reaction.get('type') === tab);
    };

    const getFullName = (extensionKey: ExtensionKey) =>
        `${extensions.getIn([extensionKey, 'contact', 'firstName'])} ${extensions.getIn([extensionKey, 'contact', 'lastName'])}`;

    return (
        <BaseModal
            className="h-[440px] bg-gray-100"
            open={true}
            header={i18next.t('reactions.modal.title', {
                count: totalReactions,
            })}
            disableActions={true}
        >
            <>
                <div className="flex flex-wrap gap-1">
                    <button
                        className={twMerge(
                            'w-16 rounded-sm border px-1',
                            tab === 'all'
                                ? 'bg-gray-200 hover:bg-gray-300'
                                : 'hover:bg-gray-200',
                        )}
                        onClick={() => setTab('all')}
                    >
                        {i18next.t('reactions.modal.all')}
                    </button>

                    {reactions
                        .filter((reaction) => reaction.get('count') > 0)
                        .map((reaction) => (
                            <button
                                key={reaction.get('type')}
                                className={twMerge(
                                    'w-16 rounded-sm border px-1 whitespace-nowrap',
                                    tab === reaction.get('type')
                                        ? 'bg-gray-200 hover:bg-gray-300'
                                        : 'hover:bg-gray-200',
                                )}
                                onClick={() => {
                                    setTab(reaction.get('type'));
                                }}
                            >
                                <em-emoji shortcodes={reaction.get('type')} />{' '}
                                {reaction.get('count')}
                            </button>
                        ))}
                </div>

                <div className="my-4 flex max-h-64 flex-col gap-2 overflow-y-scroll">
                    {getOwnReactions().size > 0 ? (
                        <div className="mb-2">
                            {getOwnReactions().map((reaction) => (
                                <button
                                    key={reaction.get('type')}
                                    className="focus-ring-inset flex w-full justify-between rounded-sm px-2 py-1 text-start hover:bg-gray-200"
                                    title={i18next.t(
                                        'reactions.modal.click_to_remove',
                                    )}
                                    onClick={() =>
                                        dispatch(
                                            deleteReaction({
                                                chatSessionKey:
                                                    keys.chatSessionKey,
                                                chatMessageKey:
                                                    keys.chatMessageKey,
                                                shortcode: reaction.get('type'),
                                                threadParentKey:
                                                    keys.threadParentKey,
                                            }),
                                        )
                                    }
                                >
                                    <div className="text-chatYou font-bold">
                                        {i18next.t('chat.you')}
                                    </div>

                                    <em-emoji
                                        shortcodes={reaction.get('type')}
                                    />
                                </button>
                            ))}
                        </div>
                    ) : null}

                    <div className="flex flex-col gap-1">
                        {getReactors()
                            .sort((a, b) =>
                                a.get('count') > b.get('count') ? -1 : 1,
                            )
                            .map((reaction) =>
                                reaction
                                    .get('users')
                                    .filter(
                                        (extensionKey) =>
                                            extensionKey !== ownExtensionKey,
                                    )
                                    .map((extensionKey) => (
                                        <div
                                            key={extensionKey}
                                            className="flex justify-between rounded-sm px-2 py-1 hover:bg-gray-200"
                                        >
                                            <div>
                                                {getFullName(extensionKey)}
                                            </div>

                                            <em-emoji
                                                shortcodes={reaction.get(
                                                    'type',
                                                )}
                                            />
                                        </div>
                                    )),
                            )}
                    </div>
                </div>
            </>
        </BaseModal>
    );
};

export default ReactionsModal;
